import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlaceIcon from "@mui/icons-material/Place";
import PhoneIcon from "@mui/icons-material/Phone";
import MuseumIcon from "@mui/icons-material/Museum";
import type { SiteInfo } from "../types/site";

type HomePageProps = {
  sites: SiteInfo[];
  onSelectSite?: (site: SiteInfo) => void;
};

const HomePage: React.FC<HomePageProps> = ({ sites, onSelectSite }) => {
  const [query, setQuery] = useState("");

  const filteredSites = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return sites;

    return sites.filter((site) => {
      return (
        site.nombre.toLowerCase().includes(normalizedQuery) ||
        site.ubicacion.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query, sites]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Sitios Históricos
          </Typography>
          <Typography color="text.secondary">
            Explora el catálogo completo y selecciona un sitio para ver más
            detalles.
          </Typography>
        </Box>

        <TextField
          fullWidth
          placeholder="Buscar por nombre o ubicación"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Grid container spacing={3}>
          {filteredSites.map((site) => (
            <Grid key={site.id}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MuseumIcon color="primary" />
                        <Typography variant="h6" fontWeight={600}>
                          {site.nombre}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <PlaceIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          {site.ubicacion}
                        </Typography>
                      </Stack>

                      {site.telefono && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon color="action" fontSize="small" />
                          <Typography variant="body2" color="text.secondary">
                            {site.telefono}
                          </Typography>
                        </Stack>
                      )}

                      {site.detalle && (
                        <Typography variant="body2" color="text.secondary">
                          {site.detalle}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`ID: ${site.id}`} size="small" />
                        <Chip
                          label={`Lat: ${site.latitud.toFixed(2)}`}
                          size="small"
                        />
                        <Chip
                          label={`Lng: ${site.longitud.toFixed(2)}`}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onSelectSite && onSelectSite(site)}
                  >
                    Ver detalles
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredSites.length === 0 && sites.length > 0 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No se encontraron sitios para “{query}”.
            </Typography>
          </Box>
        )}

        {sites.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No hay sitios disponibles por el momento.
            </Typography>
          </Box>
        )}
      </Stack>
    </Container>
  );
};

export default HomePage;
